// Geometry helpers for the environment builders (§12.5): primitives placed in sim coordinates,
// vertex colors with a baked vertical ambient-occlusion ramp, merging per material bucket
// (`BufferGeometryUtils.mergeGeometries`) and crack-free smooth-normal hulls for outlines.
import {
  BufferAttribute, BufferGeometry, Color, ExtrudeGeometry, Float32BufferAttribute, LatheGeometry, Matrix4, Quaternion, Shape, ShapeUtils, Vector2,
  Vector3, type ColorRepresentation,
} from 'three';
import { mergeGeometries, mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

const tmpColor = new Color();
const tmpQ = new Quaternion();
const tmpV = new Vector3();
const UP = new Vector3(0, 1, 0);

export interface PrepOptions {
  color?: ColorRepresentation;
  /** Baked vertical occlusion: colors darken to `dark` (multiplier) at three.js y ≤ y0, full at y ≥ y1. */
  ao?: readonly [number, number, number];
  /** Keep the uv attribute (merged buckets must agree). */
  uv?: boolean;
  /** Keep the geometry's own color attribute (multiplied by `color` and the occlusion). */
  keepColor?: boolean;
}

/** Non-indexed copy of `geo` (transformed by `m`) with position, normal, color and optionally uv. */
export function prep(geo: BufferGeometry, m: Matrix4 | null, o: PrepOptions = {}): BufferGeometry {
  let g = geo.index ? geo.toNonIndexed() : geo.clone();
  if (m) g.applyMatrix4(m);
  if (!g.getAttribute('normal')) g.computeVertexNormals();
  const own = o.keepColor ? g.getAttribute('color') : undefined;
  for (const name of Object.keys(g.attributes)) {
    if (name !== 'position' && name !== 'normal' && !(o.uv && name === 'uv')) g.deleteAttribute(name);
  }
  if (o.uv && !g.getAttribute('uv')) g.setAttribute('uv', new Float32BufferAttribute(new Float32Array(g.getAttribute('position').count * 2), 2));
  const pos = g.getAttribute('position');
  const col = new Float32Array(pos.count * 3);
  tmpColor.set(o.color ?? '#FFFFFF');
  for (let i = 0; i < pos.count; i++) {
    let k = 1;
    if (o.ao) {
      const [y0, y1, dark] = o.ao;
      const t = Math.min(1, Math.max(0, (pos.getY(i) - y0) / Math.max(1e-6, y1 - y0)));
      k = dark + (1 - dark) * t * t * (3 - 2 * t);
    }
    col[i * 3] = tmpColor.r * k * (own ? own.getX(i) : 1);
    col[i * 3 + 1] = tmpColor.g * k * (own ? own.getY(i) : 1);
    col[i * 3 + 2] = tmpColor.b * k * (own ? own.getZ(i) : 1);
  }
  g.setAttribute('color', new BufferAttribute(col, 3));
  g.groups.length = 0;
  if (g !== geo) geo.dispose();
  return g;
}

/** Merges prepared geometries (all non-indexed, same attributes). */
export function merge(parts: BufferGeometry[]): BufferGeometry {
  if (parts.length === 0) return new BufferGeometry();
  const g = mergeGeometries(parts, false);
  if (!g) throw new Error('environment merge failed (mismatched attributes)');
  for (const p of parts) p.dispose();
  g.computeBoundingSphere();
  g.computeBoundingBox();
  return g;
}

/** Merges geometries keeping only `attrs` (all parts must have them). */
export function mergeKeep(parts: BufferGeometry[], attrs: readonly string[]): BufferGeometry {
  const list = parts.map((g) => {
    const n = g.index ? g.toNonIndexed() : g;
    if (n !== g) g.dispose();
    for (const k of Object.keys(n.attributes)) if (!attrs.includes(k)) n.deleteAttribute(k);
    n.groups.length = 0;
    return n;
  });
  const out = mergeGeometries(list, false);
  if (!out) throw new Error('environment merge failed (mismatched attributes)');
  for (const g of list) g.dispose();
  out.computeBoundingSphere();
  return out;
}

/**
 * A hull for inverted-hull outlines: positions welded and normals averaged, so flat-shaded prisms
 * and boxes inflate without cracks at their corners.
 */
export function hullGeometry(geo: BufferGeometry): BufferGeometry {
  const g = new BufferGeometry();
  const src = geo.index ? geo.toNonIndexed() : geo;
  g.setAttribute('position', src.getAttribute('position').clone());
  if (src !== geo) src.dispose();
  const welded = mergeVertices(g, 1e-3);
  g.dispose();
  welded.computeVertexNormals();
  welded.computeBoundingSphere();
  return welded;
}

/** Transform in three.js space from sim position (x, y, z up) and a yaw for local +X (sim angle). */
export function simMatrix(x: number, y: number, z: number, angle = 0, sx = 1, sy = 1, sz = 1, out = new Matrix4()): Matrix4 {
  tmpQ.setFromAxisAngle(UP, -angle);
  return out.compose(tmpV.set(x, z, y), tmpQ, new Vector3(sx, sy, sz));
}

/** A lathe from (radius, height) pairs in three.js units, revolved about +Y. */
export function lathe(profile: readonly (readonly [number, number])[], segments: number, phiStart = 0, phiLength = Math.PI * 2): LatheGeometry {
  return new LatheGeometry(profile.map(([r, h]) => new Vector2(r, h)), segments, phiStart, phiLength);
}

/** A profile point (radius, three.js height). */
export type ProfilePoint = readonly [number, number];

/**
 * Revolves a cross-section about +Y over the sim angles [a0, a1] (sim angle 0 = +x, increasing
 * toward sim +y = three.js +z). `runs` are the smooth stretches of the cross-section, traversed
 * counter-clockwise in the (radius right, height up) plane so normals face out; each run is lathed
 * on its own, so the corners between runs stay crisp. With `cap` the sector's two radial ends are
 * closed with the whole cross-section (a wedge piece); a full revolution needs no caps.
 * A clockwise cross-section (signed area < 0 when closed through the axis) is reversed, so
 * a profile written top-down still faces out instead of being back-face culled.
 */
export function sweep(runs: readonly (readonly ProfilePoint[])[], segments: number, a0 = 0, a1 = Math.PI * 2, cap = false): BufferGeometry {
  if (profileArea(runs) < 0) {
    runs = runs.map((run) => run.slice().reverse()).reverse();
  }
  const parts: BufferGeometry[] = [];
  for (const run of runs) {
    const g = new LatheGeometry(run.map(([r, h]) => new Vector2(Math.max(r, 1e-4), h)), segments, Math.PI / 2 - a1, a1 - a0);
    parts.push(g.toNonIndexed());
    g.dispose();
  }
  if (cap) {
    const contour: Vector2[] = [];
    for (const run of runs) for (const p of run) {
      const last = contour[contour.length - 1];
      if (!last || Math.abs(last.x - p[0]) > 1e-6 || Math.abs(last.y - p[1]) > 1e-6) contour.push(new Vector2(p[0], p[1]));
    }
    if (contour.length > 2 && contour[0].distanceTo(contour[contour.length - 1]) < 1e-6) contour.pop();
    const tris = ShapeUtils.triangulateShape(contour, []);
    for (const [a, sign] of [[a0, -1], [a1, 1]] as const) {
      const pos: number[] = [];
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      // Outward normal of this end: −tangent at a0, +tangent at a1 (three.js x, z).
      const nx = -sa * sign;
      const nz = ca * sign;
      for (const t of tris) {
        const v = t.map((i) => new Vector3(contour[i].x * ca, contour[i].y, contour[i].x * sa));
        const n = new Vector3().subVectors(v[1], v[0]).cross(new Vector3().subVectors(v[2], v[0]));
        if (n.x * nx + n.z * nz < 0) v.reverse();
        for (const p of v) pos.push(p.x, p.y, p.z);
      }
      const g = new BufferGeometry();
      g.setAttribute('position', new Float32BufferAttribute(pos, 3));
      g.setAttribute('uv', new Float32BufferAttribute(new Float32Array((pos.length / 3) * 2), 2));
      g.computeVertexNormals();
      parts.push(g);
    }
  }
  for (const p of parts) for (const k of Object.keys(p.attributes)) if (k !== 'position' && k !== 'normal' && k !== 'uv') p.deleteAttribute(k);
  const merged = mergeGeometries(parts, false);
  for (const p of parts) p.dispose();
  if (!merged) throw new Error('sweep merge failed');
  merged.computeBoundingSphere();
  return merged;
}

/**
 * Signed area (shoelace, radius right / height up) of a cross-section closed through the axis
 * (its ends are joined by their projections onto r = 0): positive when counter-clockwise, i.e.
 * when a sweep of it faces outward (away from the solid it bounds).
 */
export function profileArea(runs: readonly (readonly ProfilePoint[])[]): number {
  const pts: ProfilePoint[] = [];
  for (const run of runs) for (const p of run) pts.push(p);
  if (pts.length < 2) return 0;
  pts.push([0, pts[pts.length - 1][1]], [0, pts[0][1]]);
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x0, y0] = pts[i];
    const [x1, y1] = pts[(i + 1) % pts.length];
    a += x0 * y1 - x1 * y0;
  }
  return a / 2;
}

/** A rounded rectangle shape centered at the origin in the XY plane. */
export function roundedRectShape(hx: number, hy: number, r: number): Shape {
  const s = new Shape();
  s.moveTo(-hx + r, -hy);
  s.lineTo(hx - r, -hy);
  s.quadraticCurveTo(hx, -hy, hx, -hy + r);
  s.lineTo(hx, hy - r);
  s.quadraticCurveTo(hx, hy, hx - r, hy);
  s.lineTo(-hx + r, hy);
  s.quadraticCurveTo(-hx, hy, -hx, hy - r);
  s.lineTo(-hx, -hy + r);
  s.quadraticCurveTo(-hx, -hy, -hx + r, -hy);
  return s;
}

/** A polygon shape from sim points (x, y). */
export function polygonShape(points: readonly { x: number; y: number }[]): Shape {
  const s = new Shape();
  points.forEach((p, i) => (i === 0 ? s.moveTo(p.x, p.y) : s.lineTo(p.x, p.y)));
  s.closePath();
  return s;
}

/**
 * Extrudes a sim-plane shape (x, y) into a slab whose top sits at three.js y = `top`, `depth`
 * thick, with an optional rounded bevel on the top and bottom edges.
 */
export function slab(shape: Shape, top: number, depth: number, bevel = 0, curveSegments = 24, bevelSegments = 3): BufferGeometry {
  const g = new ExtrudeGeometry(shape, {
    depth: Math.max(0.001, depth - 2 * bevel), bevelEnabled: bevel > 0, bevelThickness: bevel, bevelSize: bevel,
    bevelOffset: -bevel, bevelSegments, curveSegments,
  });
  // Shape XY → sim XY, extrusion +Z → down. three.js (x, y, z) = (sim x, up, sim y).
  g.applyMatrix4(new Matrix4().set(
    1, 0, 0, 0,
    0, 0, -1, top - bevel,
    0, 1, 0, 0,
    0, 0, 0, 1,
  ));
  g.computeVertexNormals();
  return g;
}

/** Flat-top hexagon (circumradius r) as a shape in the sim plane. */
export function hexShape(r: number): Shape {
  const s = new Shape();
  for (let k = 0; k < 6; k++) {
    const a = (k * Math.PI) / 3;
    if (k === 0) s.moveTo(r * Math.cos(a), r * Math.sin(a));
    else s.lineTo(r * Math.cos(a), r * Math.sin(a));
  }
  s.closePath();
  return s;
}

/** An annulus sector in the sim plane between radii r0..r1 spanning angles a0..a1. */
export function sectorShape(r0: number, r1: number, a0: number, a1: number, steps: number): Shape {
  const s = new Shape();
  for (let i = 0; i <= steps; i++) {
    const a = a0 + ((a1 - a0) * i) / steps;
    if (i === 0) s.moveTo(r1 * Math.cos(a), r1 * Math.sin(a));
    else s.lineTo(r1 * Math.cos(a), r1 * Math.sin(a));
  }
  for (let i = steps; i >= 0; i--) {
    const a = a0 + ((a1 - a0) * i) / steps;
    s.lineTo(r0 * Math.cos(a), r0 * Math.sin(a));
  }
  s.closePath();
  return s;
}

/** Deterministic PRNG (mulberry32) for scattering props from the round seed. */
export function seeded(seed: number): () => number {
  let a = (seed ^ 0x9e3779b9) >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Collects prepared parts per bucket (one material each) and merges them. */
export class Batch {
  private readonly buckets = new Map<string, BufferGeometry[]>();

  add(bucket: string, geo: BufferGeometry, m: Matrix4 | null, o: PrepOptions = {}): void {
    let list = this.buckets.get(bucket);
    if (!list) {
      list = [];
      this.buckets.set(bucket, list);
    }
    list.push(prep(geo, m, o));
  }

  has(bucket: string): boolean {
    return (this.buckets.get(bucket)?.length ?? 0) > 0;
  }

  /** Merges and removes one bucket. */
  take(bucket: string): BufferGeometry | null {
    const list = this.buckets.get(bucket);
    this.buckets.delete(bucket);
    return list && list.length > 0 ? merge(list) : null;
  }
}
